import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldAlert, RefreshCcw } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { toast } from "sonner";

const REASON_LABEL = {
  invalid_credentials: "Invalid credentials",
  account_disabled: "Account disabled",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function shortenUA(ua) {
  if (!ua) return "—";
  // Extract most useful bits: browser + OS
  const os = ua.match(/\((.*?)\)/)?.[1]?.split(";")?.[0]?.trim() || "";
  const browser =
    ua.match(/(Chrome|Firefox|Safari|Edge|OPR|Version)\/[\d.]+/)?.[0] || "";
  const combined = [browser, os].filter(Boolean).join(" · ");
  return combined || ua.slice(0, 60);
}

export default function AdminSecurityLog() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_24h: 0, failures_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [emailFilter, setEmailFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (onlyFailures) params.set("only_failures", "true");
      if (emailFilter.trim()) params.set("email", emailFilter.trim());
      const { data } = await api.get(`/admin/login-audit?${params.toString()}`);
      setEntries(data.entries || []);
      setSummary(data.summary || { total_24h: 0, failures_24h: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load login audit");
    } finally {
      setLoading(false);
    }
  }, [onlyFailures, emailFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="page-admin-security-log">
      <AdminHeader
        breadcrumb="Platform"
        title="Security Log"
        subtitle="Every admin sign-in — successful and failed — with source IP and device signature. Kept for internal audit only."
      />
      <div className="p-4 md:p-8 space-y-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="admin-card" data-testid="security-summary-total">
            <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">
              Sign-in attempts · last 24h
            </div>
            <div className="mt-2 font-editorial text-4xl text-altura-espresso">
              {summary.total_24h}
            </div>
          </div>
          <div className="admin-card" data-testid="security-summary-failures">
            <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">
              Failed attempts · last 24h
            </div>
            <div
              className={`mt-2 font-editorial text-4xl ${
                summary.failures_24h > 0
                  ? "text-altura-burgundy"
                  : "text-altura-espresso"
              }`}
            >
              {summary.failures_24h}
            </div>
          </div>
          <div className="admin-card flex flex-col justify-between">
            <div className="text-[10px] uppercase tracking-[0.24em] text-altura-charcoal/50">
              Actions
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-altura-espresso/20 px-4 py-2 text-xs uppercase tracking-widest text-altura-espresso hover:border-altura-gold hover:text-altura-gold transition disabled:opacity-50"
              data-testid="security-refresh"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-card flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">
              Filter by email
            </div>
            <input
              className="admin-input mt-1"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="name@domain.com"
              data-testid="security-filter-email"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-altura-espresso pb-2">
            <input
              type="checkbox"
              checked={onlyFailures}
              onChange={(e) => setOnlyFailures(e.target.checked)}
              data-testid="security-filter-failures-only"
            />
            Failures only
          </label>
        </div>

        {/* Table — desktop */}
        <div className="admin-card overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.15em] text-altura-charcoal/50">
              <tr className="border-b border-altura-espresso/10">
                <th className="text-left py-3 px-2 w-10"></th>
                <th className="text-left py-3 px-2">When</th>
                <th className="text-left py-3 px-2">Email</th>
                <th className="text-left py-3 px-2">Outcome</th>
                <th className="text-left py-3 px-2">IP</th>
                <th className="text-left py-3 px-2">Device</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.id}
                  className="border-b border-altura-espresso/5"
                  data-testid={`security-row-${e.id}`}
                >
                  <td className="py-3 px-2">
                    {e.success ? (
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <ShieldAlert className="h-4 w-4 text-altura-burgundy" />
                    )}
                  </td>
                  <td className="py-3 px-2 whitespace-nowrap text-altura-espresso">
                    {fmtWhen(e.created_at)}
                  </td>
                  <td className="py-3 px-2 text-altura-charcoal/85 break-all">
                    {e.email || "—"}
                  </td>
                  <td className="py-3 px-2">
                    {e.success ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] uppercase tracking-widest text-emerald-700">
                        Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-altura-burgundy/10 px-2 py-0.5 text-[11px] uppercase tracking-widest text-altura-burgundy">
                        {REASON_LABEL[e.reason] || "Failed"}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-2 font-mono text-xs text-altura-charcoal/75 break-all">
                    {e.ip || "—"}
                  </td>
                  <td
                    className="py-3 px-2 text-xs text-altura-charcoal/70 max-w-xs truncate"
                    title={e.user_agent || ""}
                  >
                    {shortenUA(e.user_agent)}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan="6"
                    className="py-10 text-center text-altura-charcoal/50"
                  >
                    No sign-in activity in this view.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td
                    colSpan="6"
                    className="py-10 text-center text-altura-charcoal/50"
                  >
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Cards — mobile */}
        <div className="md:hidden space-y-3">
          {entries.map((e) => (
            <div
              key={e.id}
              className="admin-card"
              data-testid={`security-card-${e.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  {e.success ? (
                    <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : (
                    <ShieldAlert className="h-4 w-4 text-altura-burgundy shrink-0" />
                  )}
                  <div className="text-sm text-altura-espresso font-medium break-all">
                    {e.email || "—"}
                  </div>
                </div>
                {e.success ? (
                  <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-700">
                    Success
                  </span>
                ) : (
                  <span className="shrink-0 inline-flex items-center rounded-full bg-altura-burgundy/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-altura-burgundy">
                    {REASON_LABEL[e.reason] || "Failed"}
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">
                    When
                  </div>
                  <div className="mt-0.5 text-altura-espresso">
                    {fmtWhen(e.created_at)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">
                    IP
                  </div>
                  <div className="mt-0.5 font-mono text-altura-charcoal/80 break-all">
                    {e.ip || "—"}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">
                    Device
                  </div>
                  <div
                    className="mt-0.5 text-altura-charcoal/75"
                    title={e.user_agent || ""}
                  >
                    {shortenUA(e.user_agent)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {entries.length === 0 && !loading && (
            <div className="admin-card text-center text-sm text-altura-charcoal/50 py-10">
              No sign-in activity in this view.
            </div>
          )}
          {loading && (
            <div className="admin-card text-center text-sm text-altura-charcoal/50 py-10">
              Loading…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
